import React, { useContext } from "react"
import { ethers } from "ethers"
import { Box, Grid, useMediaQuery, useTheme, Typography } from "components/ui"
import { NextButton } from "components/ui/NextButton"
import { FormField, FormTextArea, FormTextField } from "components/ui"
import { LinearProgressLoader } from "components/ui/LinearProgress"

import { EvmResponsiveDialog } from "../components/EvmResponsiveDialog"
import { BackButton } from "modules/lite/components/BackButton"

import { useEvmProposalOps } from "services/contracts/etherlinkDAO/hooks/useEvmProposalOps"
import { useEvmDaoOps } from "services/contracts/etherlinkDAO/hooks/useEvmDaoOps"
import { EtherlinkContext } from "services/wagmi/context"
import { EvmPropTransferAssets } from "./EvmProposals/EvmPropTransferAssets"
import { EvmPropEditRegistry } from "./EvmProposals/EvmPropEditRegistry"
import { EvmPropContractCall } from "./EvmProposals/EvmPropContractCall"
import { EvmPropDaoConfig } from "./EvmProposals/EvmPropDaoConfig"
import EvmPropTokenOps from "./EvmProposals/EvmPropTokenOps"
import { EvmProposalOptions } from "../config"
import { EvmOffchainDebate } from "./EvmProposals/EvmOffchainDebate"
import { EvmPropBatchActions } from "./EvmProposals/EvmPropBatchActions"
import { EProposalType } from "../types"

import { OptionContainer, ActionText, ActionDescriptionText, TitleContainer } from "components/ui"
import { isFeatureEnabled } from "utils/features"

const renderModal = (modal: EProposalType) => {
  switch (modal) {
    case "batch_actions":
      return <EvmPropBatchActions />
    case "transfer_assets":
      return <EvmPropTransferAssets />
    case "edit_registry":
      return <EvmPropEditRegistry />
    case "contract_call":
      return <EvmPropContractCall />
    case "change_config":
      return <EvmPropDaoConfig />
    case "token_operation":
      return <EvmPropTokenOps />
    case "off_chain_debate":
      return <EvmOffchainDebate />
    default:
      return null
  }
}

export const EvmProposalsActionDialog = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const theme = useTheme()
  const { isLoading, currentStep, metadata, setMetadataFieldValue, isDeploying, isNextDisabled, nextStep, prevStep } =
    useEvmProposalOps()
  const { daoDelegate, userVotingWeight, loggedInUser, refreshTokenStats } = useEvmDaoOps()
  const { daoSelected } = useContext(EtherlinkContext)
  const [isDelegating, setIsDelegating] = React.useState(false)
  const isMobileSmall = useMediaQuery(theme.breakpoints.down("lg"))
  const offchainEnabled = isFeatureEnabled("etherlink-offchain-debate")

  // Check if this is a wrapped token DAO
  const isWrappedTokenDao =
    !!daoSelected?.underlyingToken &&
    typeof daoSelected.underlyingToken === "string" &&
    ethers.isAddress(daoSelected.underlyingToken)

  const availableOptions = React.useMemo(() => {
    let options = EvmProposalOptions
    if (!offchainEnabled) {
      options = options.filter(option => option.modal !== "off_chain_debate")
    }
    // Hide token operations for wrapped token DAOs (they can't mint/burn)
    if (isWrappedTokenDao) {
      options = options.filter(option => option.modal !== "token_operation")
    }
    return options
  }, [offchainEnabled, isWrappedTokenDao])
  React.useEffect(() => {
    if (!offchainEnabled && metadata.type === "off_chain_debate") {
      setMetadataFieldValue("type", "")
    }
  }, [offchainEnabled, metadata.type, setMetadataFieldValue])
  const proposalTitle = EvmProposalOptions.find(option => option.modal === metadata.type)?.label

  return (
    <>
      <EvmResponsiveDialog open={open} onClose={handleClose} title={"New Proposal"} template="xs">
        <TitleContainer container direction="row">
          <Typography color="textPrimary">Select Proposal Type</Typography>
        </TitleContainer>
        <Grid container spacing={2}>
          {availableOptions.map((option: any, index) => (
            <Grid
              item
              xs={isMobileSmall ? 12 : 4}
              key={index}
              onClick={() => setMetadataFieldValue("type", option.modal as any)}
            >
              <OptionContainer>
                <ActionText color={option?.is_disabled ? "textSecondary" : "textPrimary"}>{option.label}</ActionText>
                <ActionDescriptionText color={option?.is_disabled ? "textSecondary" : "textPrimary"}>
                  {option.description}
                </ActionDescriptionText>
              </OptionContainer>
            </Grid>
          ))}
        </Grid>
      </EvmResponsiveDialog>

      <EvmResponsiveDialog
        open={currentStep === 1}
        onClose={() => setMetadataFieldValue("type", "")}
        title={proposalTitle}
      >
        <div
          style={
            {
              flexWrap: "nowrap",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box"
            } as React.CSSProperties
          }
        >
          <Grid container direction="column" style={{ gap: 18, marginBottom: 32 }}>
            <FormField label="Proposal Title" labelStyle={{ fontSize: 16 }} containerStyle={{ gap: 12 }}>
              <FormTextField
                placeholder="Proposal Title"
                defaultValue={metadata.title}
                onChange={e => setMetadataFieldValue("title", e.target.value)}
                inputProps={{ style: { fontSize: 14 } }}
              />
            </FormField>

            <FormField label="Proposal Details" labelStyle={{ fontSize: 16 }} containerStyle={{ gap: 12 }}>
              <FormTextArea
                placeholder="Proposal Details"
                defaultValue={metadata.description}
                onChange={e => setMetadataFieldValue("description", e.target.value)}
                inputProps={{ style: { fontSize: 14, paddingTop: 12, paddingBottom: 12 } }}
              />
            </FormField>

            <FormField label="Discussion URL" labelStyle={{ fontSize: 16 }} containerStyle={{ gap: 12 }}>
              <FormTextField
                placeholder="Discussion URL"
                value={metadata.discussionUrl}
                onChange={e => setMetadataFieldValue("discussionUrl", (e.target.value || "").toLowerCase())}
                inputProps={{ style: { fontSize: 14 } }}
              />
            </FormField>
          </Grid>

          {/* Voting power helper */}
          <Box
            style={{
              display: "flex",
              flexDirection: isMobileSmall ? "column" : "row",
              alignItems: isMobileSmall ? "stretch" : "center",
              gap: 8,
              marginBottom: 24,
              background: theme.palette.primary.main,
              borderRadius: 4,
              padding: 16
            }}
          >
            <Typography color="textPrimary" style={{ flex: 1 }}>
              Voting power:
            </Typography>
            <NextButton
              disabled={isDelegating || !loggedInUser?.address || (userVotingWeight || 0) > 0}
              onClick={() => {
                if (!loggedInUser?.address) return
                setIsDelegating(true)
                daoDelegate(loggedInUser.address)
                  .then(() => refreshTokenStats())
                  .catch(() => {})
                  .finally(() => setIsDelegating(false))
              }}
            >
              {(userVotingWeight || 0) > 0
                ? "Voting Power Ready"
                : isDelegating
                  ? "Delegating..."
                  : "Self‑delegate (Claim Voting Power)"}
            </NextButton>
          </Box>

          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <BackButton onClick={prevStep.handler} />
            <NextButton onClick={nextStep.handler}>{nextStep.text}</NextButton>
          </Grid>
        </div>
      </EvmResponsiveDialog>

      <EvmResponsiveDialog
        template="md"
        open={currentStep >= 2}
        onClose={() => setMetadataFieldValue("type", "")}
        title={proposalTitle}
      >
        <div
          className="hb-wrapper"
          style={{
            flexWrap: "nowrap",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            maxHeight: "calc(100vh - 250px)"
          }}
        >
          <Box
            style={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0
            }}
          >
            {isDeploying ? (
              <>
                <Typography>Deploying Proposal...</Typography>
                <br />
                <LinearProgressLoader />
              </>
            ) : (
              renderModal(metadata.type as EProposalType)
            )}
          </Box>
          <Box
            style={{
              borderTop: `1px solid ${theme.palette.divider}`,
              paddingTop: 20,
              marginTop: 20,
              flexShrink: 0
            }}
          >
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <BackButton disabled={isDeploying} onClick={prevStep.handler} />
              <NextButton disabled={isLoading || isDeploying || isNextDisabled} onClick={nextStep.handler}>
                {nextStep.text}
              </NextButton>
            </Grid>
          </Box>
        </div>
      </EvmResponsiveDialog>
    </>
  )
}
